/**
 * Wave 8 — distinctive per-user color for calendar/scheduler views.
 *
 * Two ways to get a user's color:
 *  (a) `colorFor(userId)` — synchronous, derived from the same hash the
 *      backend uses (`/api/users/me/color`). Stable forever.
 *  (b) `useUserColor()` — fetch and cache the current user's color from the
 *      backend so we honour any future override stored in DB.
 */

export const USER_COLOR_PALETTE = [
  "#1FB6C1", "#A78BFA", "#F59E0B", "#10B981", "#F43F5E", "#06B6D4",
  "#FB7185", "#FBBF24", "#34D399", "#818CF8", "#F472B6", "#22D3EE",
];

// Wave 22 — light-mode palette: each entry is the readable -700/-800 sibling
// of the dark-mode entry at the same index.
export const USER_COLOR_PALETTE_LIGHT = [
  "#0d6e75", "#5b21b6", "#b45309", "#047857", "#be123c", "#0e7490",
  "#be123c", "#92400e", "#047857", "#3730a3", "#9d174d", "#0e7490",
];

function isLightTheme() {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute("data-theme") === "light";
}

export function colorFor(userId) {
  const palette = isLightTheme() ? USER_COLOR_PALETTE_LIGHT : USER_COLOR_PALETTE;
  if (!userId) return palette[0];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h += userId.charCodeAt(i);
  return palette[h % palette.length];
}

/** A tiny pill that shows the user's name in their colour. */
export function UserChip({ user, compact = false, testId }) {
  if (!user) return null;
  const c = user.color || colorFor(user.id);
  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${
        compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
      }`}
      style={{ background: `${c}20`, border: `1px solid ${c}55`, color: c }}
      data-testid={testId}
    >
      <span
        className="rounded-full flex items-center justify-center font-bold"
        style={{
          background: c,
          color: "#0a0b0e",
          width: compact ? "14px" : "18px",
          height: compact ? "14px" : "18px",
          fontSize: compact ? "8px" : "10px",
        }}
      >
        {initial}
      </span>
      {!compact && <span className="truncate max-w-[120px]">{user.name || user.email}</span>}
    </span>
  );
}
